import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.REACT_APP_API_URL || '/api',
  // Bumped from 10s to 30s while backend aggregate cache (issue #116) warms on
  // cold starts. Fast warm reads still complete in well under a second.
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isDevelopment = import.meta.env.DEV;
const unsafeMethods = new Set(['post', 'put', 'patch', 'delete']);

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const ensureCsrfToken = async () => {
  if (typeof window === 'undefined') return null;

  const existing = getCookie('csrf_token');
  if (existing) return existing;

  try {
    const response = await axios.get('/csrf-token', {
      baseURL: api.defaults.baseURL,
      withCredentials: true,
    });
    return response.data?.csrf_token || getCookie('csrf_token');
  } catch (error) {
    if (isDevelopment) {
      console.error('Unable to fetch CSRF token:', error);
    }
    return null;
  }
};

// Request interceptor for logging and auth
api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};

    // Add auth token if available
    const token = typeof localStorage !== 'undefined'
      ? localStorage.getItem('access_token')
      : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = config.method?.toLowerCase();
    if (unsafeMethods.has(method) && !config.headers['X-CSRF-Token']) {
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    if (isDevelopment) {
      console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    }
    return config;
  },
  (error) => {
    if (isDevelopment) {
      console.error('Request error:', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (isDevelopment) {
      console.error('Response error:', error);

      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        console.error(`API Error ${status}:`, data);
      } else if (error.request) {
        // Request was made but no response received
        console.error('Network error:', error.request);
      } else {
        // Something else happened
        console.error('Error:', error.message);
      }
    }

    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // System endpoints
  getHealth: () => api.get('/health'),
  getStatus: () => api.get('/status'),

  // Articles endpoints
  getArticles: (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const paramKey = key === 'tags' ? 'tag' : key;
        if (Array.isArray(value)) {
          queryParams.append(paramKey, value.join(','));
        } else {
          queryParams.append(paramKey, value.toString());
        }
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `/articles?${queryString}` : '/articles';

    return api.get(url);
  },

  getArticle: (id) => api.get(`/articles/${id}`),

  // Search endpoints
  searchArticles: (searchRequest, page = 1, perPage = 20) => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });

    if (searchRequest?.query) {
      params.set('query', searchRequest.query);
    }
    if (searchRequest?.source) {
      params.set('source', searchRequest.source);
    }
    const tags = searchRequest?.tags;
    if (Array.isArray(tags) && tags.length > 0) {
      params.set('tag', tags[0]);
    } else if (typeof tags === 'string' && tags) {
      params.set('tag', tags);
    }
    if (searchRequest?.start_date) {
      params.set('from_date', searchRequest.start_date);
    }
    if (searchRequest?.end_date) {
      params.set('to_date', searchRequest.end_date);
    }

    return api.get(`/articles?${params}`);
  },

  // Metadata endpoints
  getTags: () => api.get('/tags'),
  getSources: () => api.get('/sources'),
  getFeeds: () => api.get('/feeds'),

  // Admin endpoints
  triggerPoll: () => api.post('/poll'),
  getUsers: () => api.get('/admin/users'),
  promoteUser: (userId) => api.post(`/admin/users/${userId}/promote`),
  demoteUser: (userId) => api.post(`/admin/users/${userId}/demote`),
  enableFeed: (feedName) => api.post(`/feeds/${encodeURIComponent(feedName)}/enable`),
};

// Helper functions for common operations
export const apiHelpers = {
  // Format API errors for display
  formatError: (error) => {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    } else if (error.response?.data?.message) {
      return error.response.data.message;
    } else if (error.message) {
      return error.message;
    } else {
      return 'An unexpected error occurred';
    }
  },

  // Build search parameters
  buildSearchParams: (filters) => {
    const params = {};

    if (filters.query) params.query = filters.query;
    if (filters.tags && filters.tags.length > 0) params.tag = filters.tags[0];
    if (filters.source) params.source = filters.source;
    if (filters.startDate) params.from_date = filters.startDate;
    if (filters.endDate) params.to_date = filters.endDate;
    if (filters.page) params.page = filters.page;
    if (filters.perPage) params.per_page = filters.perPage;

    return params;
  },

  // Format date for API
  formatDate: (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  },

  // Parse API date response
  parseDate: (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  },
};

export default api;
